import { useQuery } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { useSession } from '../session';
import { Notice } from '../ui/Badge';
import { Button } from '../ui/Button';
import {
  isForbidden,
  refreshFailed,
  StaleNotice,
  shownState,
  unresolved,
  UnresolvedState,
} from '../ui/QueryStates';
import { IconInventory } from '../icons';

export function usePricingRaisedCount() {
  const session = useSession(),
    trpc = useTRPC(),
    allowed = session.role === 'owner' || session.role === 'office';
  const query = useQuery({
    ...trpc.pricing.raisedCount.queryOptions(),
    enabled: allowed,
    retry: false,
  });
  return {
    query,
    allowed,
    data: allowed && !isForbidden(query.error) ? query.data : undefined,
  };
}
export function PricingRaiseBadge() {
  const { allowed, data, query } = usePricingRaisedCount();
  if (!allowed || isForbidden(query.error)) return null;
  if (!data)
    return (
      <span
        className="ml-auto text-xs text-muted-foreground"
        aria-label="Price raise count unavailable"
      >
        —
      </span>
    );
  if (!data.raiseCount) return null;
  return (
    <span
      className="ml-auto rounded-full bg-primary/10 px-2 text-xs tabular-nums"
      aria-label={`${data.raiseCount} unreviewed price raises${refreshFailed(query, data) ? ', last saved count' : ''}`}
    >
      {data.raiseCount}
    </span>
  );
}
export function PricingRaisedNotice({
  onOpen,
}: {
  onOpen: (opener: HTMLButtonElement) => void;
}) {
  const { allowed, data, query } = usePricingRaisedCount();
  if (!allowed || isForbidden(query.error)) return null;
  if (!data)
    return (
      <UnresolvedState
        state={unresolved(shownState(query, query.data))}
        icon={IconInventory}
        what="price raise count"
        error={query.error}
        onRetry={() => void query.refetch()}
        skeleton="h-16"
      />
    );
  return (
    <div className="space-y-2">
      <StaleNotice
        show={refreshFailed(query, data)}
        onRetry={() => void query.refetch()}
      />
      {data.raiseCount > 0 ? (
        <Notice tone="info">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p>
              {data.raiseCount} unreviewed automatic{' '}
              {data.raiseCount === 1 ? 'price raise' : 'price raises'} across{' '}
              {data.partCount} {data.partCount === 1 ? 'part' : 'parts'}{' '}
              {data.raiseCount === 1 ? 'needs' : 'need'} review.
            </p>
            <Button className="min-h-11" onClick={(e) => onOpen(e.currentTarget)}>
              Review price raises
            </Button>
          </div>
        </Notice>
      ) : (
        <p className="text-sm text-muted-foreground">
          No unreviewed automatic price raises.
        </p>
      )}
    </div>
  );
}
